import React, { useEffect, useRef } from "react";

function rand(min, max) {
  return Math.random() * (max - min) + min;
}

function randInt(min, max) {
  return Math.floor(rand(min, max + 1));
}

function pick(arr) {
  return arr[randInt(0, arr.length - 1)];
}

export default function CaptchaCanvas({
  text,
  width = 220,
  height = 72,
  refreshSeed = 0,
  className = "",
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !text) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    drawCaptcha(ctx, text, width, height);
  }, [text, width, height, refreshSeed]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={className}
      aria-label="Captcha challenge"
      role="img"
    />
  );
}

function drawCaptcha(ctx, text, width, height) {
  ctx.clearRect(0, 0, width, height);

  drawBackground(ctx, width, height);
  drawNoiseDots(ctx, width, height, 90);
  drawNoiseArcs(ctx, width, height, 6);
  drawChars(ctx, text, width, height);
  drawStrikeLines(ctx, width, height, 3);
}

function drawBackground(ctx, width, height) {
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, "#f7f7f7");
  gradient.addColorStop(1, "#ececec");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
}

function drawNoiseDots(ctx, width, height, count) {
  for (let i = 0; i < count; i += 1) {
    ctx.beginPath();
    ctx.arc(rand(0, width), rand(0, height), rand(0.6, 1.8), 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${randInt(80, 160)}, ${randInt(80, 160)}, ${randInt(80, 160)}, ${rand(
      0.12,
      0.3,
    )})`;
    ctx.fill();
  }
}

function drawNoiseArcs(ctx, width, height, count) {
  for (let i = 0; i < count; i += 1) {
    ctx.beginPath();
    ctx.strokeStyle = `rgba(${randInt(100, 170)}, ${randInt(100, 170)}, ${randInt(
      100,
      170,
    )}, ${rand(0.12, 0.24)})`;
    ctx.lineWidth = rand(0.8, 1.8);
    ctx.arc(
      rand(0, width),
      rand(0, height),
      rand(8, 28),
      rand(0, Math.PI),
      rand(Math.PI, Math.PI * 2),
    );
    ctx.stroke();
  }
}

function drawChars(ctx, text, width, height) {
  const fonts = ["Arial", "Verdana", "Tahoma", "Trebuchet MS", "Georgia"];
  const chars = text.split("");
  const gap = width / (chars.length + 1);

  ctx.textBaseline = "middle";
  ctx.textAlign = "center";

  chars.forEach((char, index) => {
    const x = gap * (index + 1) + rand(-4, 4);
    const y = height / 2 + rand(-8, 8);
    const angle = rand(-0.35, 0.35);
    const fontSize = randInt(27, 35);
    const fontWeight = pick([600, 700, 800]);

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);

    ctx.font = `${fontWeight} ${fontSize}px ${pick(fonts)}`;
    ctx.fillStyle = `rgb(${randInt(15, 90)}, ${randInt(15, 90)}, ${randInt(15, 90)})`;

    ctx.shadowColor = `rgba(0, 0, 0, ${rand(0.08, 0.16)})`;
    ctx.shadowBlur = 1;
    ctx.shadowOffsetX = rand(-1, 1);
    ctx.shadowOffsetY = rand(-1, 1);

    ctx.fillText(char, 0, 0);

    if (Math.random() > 0.45) {
      const metrics = ctx.measureText(char);
      const underlineY = rand(6, 10);
      ctx.beginPath();
      ctx.moveTo(-(metrics.width / 2) - 2, underlineY);
      ctx.lineTo(metrics.width / 2 + 2, underlineY + rand(-1, 1));
      ctx.strokeStyle = `rgba(${randInt(40, 120)}, ${randInt(40, 120)}, ${randInt(
        40,
        120,
      )}, ${rand(0.18, 0.35)})`;
      ctx.lineWidth = rand(0.8, 1.5);
      ctx.stroke();
    }

    ctx.restore();
  });
}

function drawStrikeLines(ctx, width, height, count) {
  for (let i = 0; i < count; i += 1) {
    ctx.beginPath();
    ctx.moveTo(rand(0, width * 0.18), rand(6, height - 6));
    ctx.bezierCurveTo(
      rand(width * 0.2, width * 0.45),
      rand(0, height),
      rand(width * 0.55, width * 0.8),
      rand(0, height),
      rand(width * 0.82, width),
      rand(6, height - 6),
    );
    ctx.strokeStyle = `rgba(${randInt(30, 110)}, ${randInt(30, 110)}, ${randInt(
      30,
      110,
    )}, ${rand(0.22, 0.42)})`;
    ctx.lineWidth = rand(1.1, 2.2);
    ctx.stroke();
  }
}
